import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { useIsDesktop } from '../../hooks/useIsDesktop';
import { Card } from '../Card';
import { IconFigure } from '../Icons';
import { PaginatorAsync } from '../PaginatorAsync';

import { TourneyListItems } from './TourneyListItems';

import { useMinileagueData } from '@/hooks/useMinileagueData';
import { PropsWithClassName } from '@/types';
import { GenericItemTourneysData } from '@/types/tda';
import { EnumContextTourneys } from '@/types/team';
import { cn } from '@/utils';

interface Props extends PropsWithClassName, GenericItemTourneysData {
  context?: EnumContextTourneys;
  list: GenericItemTourneysData[];
  paginator?: boolean;
  classNameWrapperList?: string;
  classPaginator?: string;
  loadMore?: (pageNumber?: number) => void;
  hasMore?: boolean;
  classNames?: {
    secondaryText?: string;
    subtitle?: string;
    titleContainer?: string;
  };
  headerAction?: ReactNode;
}

const ITEM_PER_PAGE_DESKTOP = 5;

export const TourneyList = React.memo(
  ({
    title,
    className,
    list,
    action,
    handleSubmit,
    loadMore,
    variant,
    context,
    titleIcon,
    paginator,
    classNameWrapperList,
    classPaginator,
    hasMore,
    classNames = {},
    headerAction,
    withAvatar,
  }: Props) => {
    const { isDesktop, isMobile } = useIsDesktop();
    const [currentPage, setCurrentPage] = useState(0);
    const { miniligueData, handleSetMiniligueData } = useMinileagueData();
    const itemsPerPage = isDesktop ? ITEM_PER_PAGE_DESKTOP : list.length;

    const totalPages = Math.ceil(list.length / itemsPerPage);

    const paginatedList = useMemo(() => {
      const startIndex = currentPage * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return list.slice(startIndex, endIndex);
    }, [list, currentPage, itemsPerPage]);

    useEffect(() => {
      setCurrentPage(0);
    }, []);

    // FIX si cambia la cantidad de items va decreciendo y se decrementa la pagina actual
    useEffect(() => {
      if (currentPage >= totalPages && totalPages > 0) {
        setCurrentPage(totalPages - 1);
      }
    }, [currentPage, totalPages]);

    const handlePageChange = (selectedItem: { selected: number }) => {
      setCurrentPage(selectedItem.selected);
      if (loadMore) {
        loadMore(selectedItem.selected + 1);
      }
    };

    const Row = useCallback(
      ({ item, index }: { item: GenericItemTourneysData; index: number }) => {
        return (
          <TourneyListItems
            key={`${item.id}-${index}`}
            action={action || item.action}
            classNames={classNames}
            context={context}
            disabled={item.disabled}
            estadistica={item.estadistica}
            id={item?.id}
            image={item.image}
            isVip={item.isVip}
            profileName={item.profileName}
            profilePicUrl={item.profilePicUrl}
            secundaryText={item.secundaryText}
            status={item.status}
            subtitle={item.subtitle || ''}
            textCentered={item.textCentered}
            title={item.title}
            variant={variant}
            withAvatar={withAvatar}
            onClick={(params) => {
              if (item?.id && handleSubmit) {
                handleSubmit(params);
              }
              handleSetMiniligueData({
                ...miniligueData,
                miniLiga: {
                  ...miniligueData.miniLiga,
                  id: item.id,
                },
              });
            }}
          />
        );
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    const renderList = useMemo(() => {
      const shouldShowInfiniteScroll = paginator && isMobile && list.length > 5;
      const showPaginator = paginator && isDesktop && list.length > 5;

      if (shouldShowInfiniteScroll) {
        return (
          <InfiniteScroll
            className={'!h-[500px]'}
            dataLength={list.length}
            hasMore={Boolean(hasMore)}
            loader={<></>}
            next={() => loadMore && loadMore()}
          >
            {list.map((item: GenericItemTourneysData, index) => (
              <Row key={`${index}-${item.id}`} index={index} item={item} />
            ))}
          </InfiniteScroll>
        );
      }

      const selectList = showPaginator ? paginatedList : list;

      return selectList.map((item: GenericItemTourneysData, index) => (
        <Row key={`${index}-${item.id}`} index={index} item={item} />
      ));
    }, [
      Row,
      hasMore,
      isDesktop,
      isMobile,
      list,
      loadMore,
      paginatedList,
      paginator,
    ]);

    return (
      <div className={className}>
        {titleIcon && (
          <>
            <div
              className={cn(
                'flex justify-between items-center pr-9 mb-3',
                classNames.titleContainer
              )}
            >
              <p className="text-sm font-bold text-gdt-gray-2">{title}</p>
              <div className="flex justify-around items-center">
                <figure className="w-5 mr-1">
                  <IconFigure />
                </figure>
                <p className="text-sm text-gdt-gray-3 font-semibold">
                  Torneo VIP
                </p>
              </div>
            </div>
            {headerAction && list.length > 1 && headerAction}
          </>
        )}
        {!titleIcon && (
          <div
            className={cn(
              'flex justify-between items-center pr-4',
              classNames.titleContainer
            )}
          >
            <p className="text-sm font-bold text-gdt-gray-2 mb-3">{title}</p>
            {headerAction && list.length > 1 && headerAction}
          </div>
        )}
        <Card
          className={cn(
            'space-y-3 p-0 w-full overflow-hidden bg-transparent',
            classNameWrapperList,
            {
              'bg-gdt-gray-7 !space-y-[1px]':
                context &&
                [
                  EnumContextTourneys.Sidebar,
                  EnumContextTourneys.Admin,
                  EnumContextTourneys.MainContentHome,
                ].includes(context) &&
                isDesktop,
            }
          )}
        >
          {renderList}

          {isDesktop && paginator && list.length > itemsPerPage && (
            <PaginatorAsync
              key={currentPage}
              className={classPaginator}
              currentPage={currentPage}
              pageCount={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </Card>
      </div>
    );
  }
);

TourneyList.displayName = 'TourneyList';
